import type { ExpenseTaxMode } from '../types/expenses';

const INCLUDED_TAX_PERCENTAGE = 16;

export interface ExpenseTaxBreakdown {
  totalAmount: number;
  subtotalAmount: number;
  taxAmount: number;
  taxPercentage: number | null;
}

/**
 * Fase 18B: espejo en frontend de `calculateExpenseTaxBreakdown` del backend — usado solo para la
 * vista previa en vivo del formulario ("Desglose: Subtotal / IVA / Total"). El backend vuelve a
 * calcularlo con Prisma.Decimal antes de guardar, así que esta copia nunca es la fuente de verdad,
 * solo evita que el usuario tenga que adivinar el total antes de guardar.
 * - NONE ("Sin IVA"): el monto capturado es el total, sin impuesto.
 * - INCLUDED ("IVA incluido"): el monto capturado ya incluye 16% — se calcula el subtotal hacia atrás.
 * - CUSTOM ("Otros/Especificar"): el monto capturado es el subtotal, se suma el % indicado.
 */
export function calculateExpenseTaxBreakdown(amount: number, taxMode: ExpenseTaxMode, taxPercentage: number | null): ExpenseTaxBreakdown {
  const entered = Number.isFinite(amount) ? amount : 0;

  if (taxMode === 'NONE') {
    return { totalAmount: round2(entered), subtotalAmount: round2(entered), taxAmount: 0, taxPercentage: null };
  }

  if (taxMode === 'INCLUDED') {
    const subtotalAmount = round2(entered / (1 + INCLUDED_TAX_PERCENTAGE / 100));
    const taxAmount = round2(entered - subtotalAmount);
    return { totalAmount: round2(entered), subtotalAmount, taxAmount, taxPercentage: INCLUDED_TAX_PERCENTAGE };
  }

  const percentage = taxPercentage ?? 0;
  const taxAmount = round2(entered * (percentage / 100));
  return { totalAmount: round2(entered + taxAmount), subtotalAmount: round2(entered), taxAmount, taxPercentage: percentage };
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
